import React from 'react'
import { Box, IconButton } from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import Txt from './Txt'
import { APP_BAR_HEIGHT_REM } from './AppBar'
import { RESTAURANT_HEADER_HEIGHT_REM, RESTAURANT_HEADER_ZINDEX } from '../consts'
import MenuItemTile from './MenuItemTile'
import CategoryHeader from './CategoryHeader'
import OptionGroupHeader from './OptionGroupHeader'
import { useMenuByVersion } from '../queries/menuByVersion'

const sxRoot = {
	display: 'flex',
	flexDirection: 'column',
	bgcolor: '#f5f5f5',
	minHeight: '100%',
} as const
const sxTopBar = {
	// Klebt unter App-Leiste und Restaurant-Kopf, damit der Weg zurück immer sichtbar bleibt.
	position: 'sticky',
	top: `${APP_BAR_HEIGHT_REM + RESTAURANT_HEADER_HEIGHT_REM}rem`,
	zIndex: RESTAURANT_HEADER_ZINDEX - 1,
	display: 'flex',
	alignItems: 'center',
	gap: '0.5rem',
	p: '0.5rem 0.75rem',
	borderBottom: '1px solid #e0e0e0',
	bgcolor: 'white',
} as const
const sxScroll = { flex: 1, p: '1rem' } as const
const sxSection = { mb: '1.5rem' } as const
const sxGrid = {
	display: 'grid',
	// One card per row until the screen is very wide (a second column needs ~80rem); `min(…, 100%)`
	// keeps a single card from overflowing on narrow screens.
	gridTemplateColumns: 'repeat(auto-fill, minmax(min(40rem, 100%), 1fr))',
	gap: '1rem',
	alignItems: 'start',
} as const
const sxEmpty = { p: '2rem', textAlign: 'center' } as const

// Eine Menüversion im Restaurant-Admin, geöffnet aus der Menü-Historie. Abgeschaltet werden
// Artikel vom Restaurant in der Webapp, hier sind sie nur gekennzeichnet.
const MenuView = ({
	restaurantId,
	menuVersion,
	onClose,
}: {
	restaurantId?: string
	menuVersion: number | null
	// When provided, a back button is shown in the top bar.
	onClose?: () => void
}): JSX.Element => {
	const { categories, optionGroups } = useMenuByVersion({
		restaurantId: restaurantId ?? null,
		menuVersion,
	})

	return (
		<Box sx={sxRoot}>
			<Box sx={sxTopBar}>
				{onClose ? (
					<IconButton onClick={onClose} size="small">
						<ArrowBackIcon fontSize="small" />
					</IconButton>
				) : null}
				<Txt variant="subtitle1" fontWeight="bold">
					Speisekarte{menuVersion === null ? '' : ` v${menuVersion}`}
				</Txt>
			</Box>

			<Box sx={sxScroll}>
				{categories.length === 0 && optionGroups.length === 0 ? (
					<Box sx={sxEmpty}>
						<Txt color="text.secondary">Keine Einträge vorhanden</Txt>
					</Box>
				) : (
					<>
						{categories.map((category) => (
							<Box key={category.id} sx={sxSection}>
								<CategoryHeader category={category} />
								<Box sx={sxGrid}>
									{category.products.map((product) => (
										<MenuItemTile key={product.id} product={product} />
									))}
								</Box>
							</Box>
						))}
						{optionGroups.map((optionGroup) => (
							<Box key={optionGroup.id} sx={sxSection}>
								<OptionGroupHeader optionGroup={optionGroup} />
								<Box sx={sxGrid}>
									{optionGroup.options.map((option) => (
										<MenuItemTile key={option.id} option={option} />
									))}
								</Box>
							</Box>
						))}
					</>
				)}
			</Box>
		</Box>
	)
}

export default React.memo(MenuView)
